"use client";

import { useQuery } from "@tanstack/react-query";
import { API_BASE } from "./api";

// Market-intelligence data (peg status + macro odds) from dubu-api's /pro/intel endpoints.
// Server-side caches do the heavy lifting; these poll slowly and fail silently, intel is
// decoration around trading, so a missing/old API must never break the trade screen.

export interface PegStable {
  symbol: string;
  price: number;
  deviation_bps: number;
  status: "ok" | "warn" | "depeg";
}

export interface MacroItem {
  id: string;
  label: string;
  question: string;
  odds: number;
  venue: string;
  event_title: string;
}

const fetchJson = async <T>(path: string): Promise<T | null> => {
  try {
    const res = await fetch(`${API_BASE}${path}`);
    if (!res.ok) return null;
    const body = await res.json();
    return body?.success ? (body.data as T) : null;
  } catch {
    return null;
  }
};

export const usePeg = () =>
  useQuery({
    queryKey: ["intel", "peg"],
    queryFn: () => fetchJson<{ stables: PegStable[] }>("/pro/intel/peg"),
    refetchInterval: 120_000,
    staleTime: 60_000,
    retry: false,
  });

export const useMacro = () =>
  useQuery({
    queryKey: ["intel", "macro"],
    queryFn: () => fetchJson<{ items: MacroItem[] }>("/pro/intel/macro"),
    // Server refreshes one watchlist item every 2h (free-tier budget), polling faster than
    // this just re-reads the same cache.
    refetchInterval: 10 * 60_000,
    staleTime: 5 * 60_000,
    retry: false,
  });
